import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { IDropDownArray, IProfileData } from "src/types/GlobalInterfaces";
import SplashScreen from "src/Components/SplashScreen";
import BreadCrumb from "src/Components/GlobalComponents/BreadCrumb";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import SimpleInput from "src/Components/SimpleInput";
import { useForm, Controller } from "react-hook-form";
import SelectDropdown from "src/Components/GlobalComponents/SelectDropdown";
import { cityList } from "src/Config/CityList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookSquare,
  faInstagram,
  faLinkedin,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import moment from "moment";
import CustomWebsiteDropdown from "src/Components/GlobalComponents/Widget/CustomWebsiteDropdown";
import { phoneNumberRegex } from "src/lib/constans";
import { validateAndFilterPhoneNumber } from "src/helpers/helperFunctions";

const genderList: IDropDownArray[] = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "other" },
];

interface FormValues {
  firstName: string;
  lastName: string;
  date: string;
  gender: string;
  mobile: string;
  address: string;
  city: string;
  headLine: string;
  facebookLink: string;
  linkedinLink: string;
  instagramLink: string;
  whatsappLink: string;
  [key: string]: any;
}

const EditCustomerProfile: React.FC = () => {
  const { uid = "" } = useParams();
  const navigate = useNavigate();

  const [getLoader, setgetLoader] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<IProfileData | null>(null);
  const fetchCustomer = async () => {
    setgetLoader(true);
    const { res, err } = await httpRequest<IProfileData>({
      path: `/customer/get/profile/${uid}`,
    });
    if (res) {
      setProfileData(res);
    } else {
      toast(err, { type: "error" });
    }
    setgetLoader(false);
  };

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      date: "",
      gender: "",
      mobile: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "put",
      path: `/customer/admin/profile-edit/${uid}`,
      params: data,
    });
    if (res) {
      toast(res?.message || "Success!", { type: "success" });
      navigate(
        profileData?.userRole === "customer"
          ? `/customer/${uid}/view`
          : `/doctor/${uid}/view`
      );
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    if (profileData) {
      setValue("firstName", profileData?.firstName || "");
      setValue("lastName", profileData?.lastName || "");
      setValue("date", profileData?.date ? moment(profileData?.date).format("YYYY-MM-DD") : "");
      setValue("gender", profileData?.gender || "");
      setValue("mobile", profileData?.mobile || profileData?.number || "");
    }
  }, [profileData]);

  useEffect(() => {
    if (uid) {
      fetchCustomer();
    }
  }, [uid]);

  return (
    <>
      {getLoader && <SplashScreen />}
      <BreadCrumb
        current={`${profileData?.firstName || ""} ${
          profileData?.lastName || ""
        }`}
        previous={profileData?.userRole === "customer" ? "Customer" : "Doctor"}
        navigateUrl={
          profileData?.userRole === "customer" ? "/customers" : "/doctors"
        }
        btnShow={true}
        isUpsert={true}
        onPress={() =>
          navigate(
            profileData?.userRole === "customer"
              ? `/customer/${uid}/view`
              : `/doctor/${uid}/view`
          )
        }
      />
      <div className="table-container">
        <div className="parent-box">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: "First Name is required",
                  pattern: {
                    value: /^[a-zA-Z\s]+$/,
                    message: "First Name must contain only letters and spaces",
                  },
                }}
                render={({ field }) => (
                  <SimpleInput
                    isLabel={true}
                    labelText="First Name"
                    type="text"
                    inputMode="text"
                    extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                    {...field}
                    id="first-name-input"
                  />
                )}
              />
              {errors.firstName && (
                <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="mb-3">
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: "Last Name is required",
                  pattern: {
                    value: /^[a-zA-Z\s]+$/,
                    message: "Last Name must contain only letters and spaces",
                  },
                }}
                render={({ field }) => (
                  <SimpleInput
                    isLabel={true}
                    labelText="Last Name"
                    type="text"
                    inputMode="text"
                    extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                    {...field}
                    id="last-name-input"
                  />
                )}
              />
              {errors.lastName && (
                <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                  {errors.lastName.message}
                </p>
              )}
            </div>
            <div className="mb-3">
              <Controller
                name="date"
                control={control}
                rules={{
                  required: "Date of Birth is required",
                  pattern: {
                    value: /^\d{4}-\d{2}-\d{2}$/,
                    message: "Invalid date format",
                  },
                  validate: {
                    minimumAge: (value) => {
                      if (!value) return true; // Let required validation handle empty values
                      const birthDate = new Date(value);
                      const today = new Date();
                      const age = today.getFullYear() - birthDate.getFullYear();
                      const monthDiff = today.getMonth() - birthDate.getMonth();

                      // Check if birthday has occurred this year
                      if (
                        monthDiff < 0 ||
                        (monthDiff === 0 &&
                          today.getDate() < birthDate.getDate())
                      ) {
                        return (
                          age - 1 >= 15 || "Age must be at least 15 years old"
                        );
                      }

                      return age >= 15 || "Age must be at least 15 years old";
                    },
                    notFutureDate: (value) => {
                      if (!value) return true;
                      const birthDate = new Date(value);
                      const today = new Date();
                      return (
                        birthDate <= today ||
                        "Date of birth cannot be in the future"
                      );
                    },
                  },
                }}
                render={({ field }) => (
                  <SimpleInput
                    isLabel={true}
                    labelText="Date of Birth"
                    type="date"
                    max={(() => {
                      const today = new Date();
                      const maxDate = new Date(
                        today.getFullYear() - 15,
                        today.getMonth(),
                        today.getDate()
                      );
                      return maxDate.toISOString().split("T")[0];
                    })()}
                    extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                    {...field}
                    id="date-of-birth-input"
                  />
                )}
              />
              {errors.date && (
                <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                  {errors.date.message}
                </p>
              )}
            </div>
            <div className="mb-3 border_hide_and_primary_border">
              <label className="font-Poppins-Regular font-size-16px text-[#252334]">
                Gender
              </label>
              <Controller
                name="gender"
                control={control}
                rules={{ required: "Gender is required" }}
                render={({ field }) => (
                  <CustomWebsiteDropdown
                    options={genderList}
                    data={genderList.filter((v) => v?.value === field.value)}
                    setData={(e) => field.onChange(e[0]?.value || "")}
                    isMulti={false}
                    dropdownMainId="gender-input"
                  />
                )}
              />
              {errors.gender && (
                <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                  {errors.gender.message}
                </p>
              )}
            </div>
            <div className="mb-3">
              <Controller
                name="mobile"
                control={control}
                rules={{
                  required: "Mobile is required",
                  pattern: {
                    value: phoneNumberRegex,
                    message: "Invalid phone number",
                  },
                }}
                render={({ field }) => (
                  <SimpleInput
                    value={field.value || ""}
                    onChange={(value) => {
                      const { filteredValue } = validateAndFilterPhoneNumber(value);
                      setValue("mobile", filteredValue);
                    }}
                    maxLength={15}
                    minLength={10}
                    isLabel={true}
                    labelText="Phone"
                    id="edit-profile-phone-input"
                    extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                  />
                )}
              />
              {errors.mobile && (
                <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                  {errors.mobile.message}
                </p>
              )}
            </div>
            {/* gender dropdown */}
            {/* <div className="mb-3">
                            <Controller
                                name="mobile"
                                control={control}
                                rules={{ required: 'Mobile is required' }}
                                render={({ field }) => (
                                    <SimpleInput
                                        isLabel={true}
                                        labelText='Mobile'
                                        type="text"
                                        inputMode="numeric" // Suggests a numeric keyboard on mobile devices
                                        minLength={10}
                                        maxLength={15}
                                        extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.mobile && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.mobile.message}</p>}
                        </div>
                        <div className="border_hide_and_primary_border">
                            <label className="font-Poppins-Regular font-size-16px text-[#252334]">City</label>
                            <Controller
                                name="city"
                                control={control}
                                rules={{ required: 'City is required' }}
                                render={({ field }) => (
                                    <SelectDropdown
                                        options={cityList}
                                        data={cityList.filter(v => v?.value === field.value)}
                                        setData={(e) => field.onChange(e[0]?.value || '')}
                                        isMulti={false}
                                    />
                                )}
                            />
                            {errors.city && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.city.message}</p>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="">
                                <Controller
                                    name="address"
                                    control={control}
                                    rules={{ required: 'Location is required' }}
                                    render={({ field }) => (
                                        <SimpleInput
                                            isLabel={true}
                                            labelText='Location'
                                            type="text"
                                            extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                                            {...field}
                                        />
                                    )}
                                />
                                {errors.address && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.address.message}</p>}
                            </div>
                        </div>
                        <hr className='products-tabs-hr-allOrder' />
                        {profileData?.userRole === "customer" &&
                            <div>
                                <Controller
                                    name="headLine"
                                    control={control}
                                    rules={{ required: 'Headline is required' }}
                                    render={({ field }) => (
                                        <SimpleInput
                                            type="textArea"
                                            isLabel={true}
                                            labelText="Headline"
                                            isLimit={500}
                                            maxLength={500}
                                            rows={3}
                                            extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                                            {...field}
                                        />
                                    )}
                                />
                                {errors.headLine && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.headLine.message}</p>}
                            </div>
                        } */}
            {/* <hr className='products-tabs-hr-allOrder' />
                        <h2 className='title-heading font-Poppins-SemiBold font-size-20px mb-4'>Social Links</h2>
                        <div className='mb-3'>
                            <label className='font-size-18px font-Poppins-Regular flex items-center gap-2' htmlFor=""><FontAwesomeIcon className='text-[#00443F]' size="1x" icon={faFacebookSquare} />Facebook</label>
                            <Controller
                                name="facebookLink"
                                control={control}
                                rules={{
                                    required: false,
                                    pattern: {
                                        value: /^(https?:\/\/)?(www\.)?facebook\.com(\/.*)?$/,
                                        message: "Please enter a valid Facebook link."
                                    }
                                }}
                                render={({ field }) => (
                                    <SimpleInput
                                        type="url"
                                        extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.facebookLink && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.facebookLink.message}</p>}
                        </div>
                        <div className='mb-3'>
                            <label className='font-size-18px font-Poppins-Regular flex items-center gap-2' htmlFor=""><FontAwesomeIcon className='text-[#00443F]' size="1x" icon={faInstagram} />Instagram</label>
                            <Controller
                                name="instagramLink"
                                control={control}
                                rules={{
                                    required: false,
                                    pattern: {
                                        value: /^(https?:\/\/)?(www\.)?instagram\.com(\/.*)?$/,
                                        message: "Please enter a valid Instagram link."
                                    }
                                }}
                                render={({ field }) => (
                                    <SimpleInput
                                        type="url"
                                        extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.instagramLink && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.instagramLink.message}</p>}
                        </div>
                        <div className='mb-3'>
                            <label className='font-size-18px font-Poppins-Regular flex items-center gap-2' htmlFor=""><FontAwesomeIcon className='text-[#00443F]' size="1x" icon={faLinkedin} />LinkedIn</label>
                            <Controller
                                name="linkedinLink"
                                control={control}
                                rules={{
                                    required: false,
                                    pattern: {
                                        value: /^(https?:\/\/)?(www\.)?linkedin\.com(\/.*)?$/,
                                        message: "Please enter a valid LinkedIn link."
                                    }
                                }}
                                render={({ field }) => (
                                    <SimpleInput
                                        type="url"
                                        extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.linkedinLink && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.linkedinLink.message}</p>}
                        </div>
                        <div className='mb-3'>
                            <label className='font-size-18px font-Poppins-Regular flex items-center gap-2' htmlFor=""><FontAwesomeIcon className='text-[#00443F]' size="1x" icon={faWhatsapp} />WhatsApp</label>
                            <Controller
                                name="whatsappLink"
                                control={control}
                                rules={{
                                    required: false,
                                    pattern: {
                                        value: /^(https?:\/\/)?(www\.)?(web\.)?whatsapp\.com(\/.*)?$/,
                                        message: "Please enter a valid WhatsApp link."
                                    }
                                }}
                                render={({ field }) => (
                                    <SimpleInput
                                        type="url"
                                        extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.whatsappLink && <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">{errors.whatsappLink.message}</p>}
                        </div>
                        <div className="flex justify-end mt-5">
                            <PrimaryBtn
                                loader={isLoader}
                                type='submit'
                                ButtonText='Save'
                                btnExtraStyle={{ backgroundColor: "#00443f", borderRadius: "1.5rem" }}
                            />
                        </div> */}

            <PrimaryBtn
              loader={isLoader}
              type="submit"
              ButtonText="Save"
              btnExtraStyle={{
                backgroundColor: "#00443f",
                borderRadius: "1.5rem",
              }}
              id="save-btn"
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default EditCustomerProfile;
